import { useRef } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { VARIABLES } from "./blocks";

const EMOJIS = ["😀", "🎉", "🍝", "🍷", "🎂", "⭐", "❤️", "👋", "✅", "📅", "🕐", "🎁"];

export default function MessageEditor({ value, onChange, channel, linkedCouponName }) {
  const bodyRef = useRef(null);
  const set = (k, v) => onChange({ ...value, [k]: v });
  const fileRef = useRef(null);

  const uploadImage = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const resp = await api.post("/settings/email-images", { data: dataUrl, content_type: file.type });
      set("attachment_url", `${process.env.REACT_APP_BACKEND_URL}${resp.data.url}`);
      toast.success("Immagine caricata: sarà visibile nell'email");
    } catch (err) { toast.error(apiError(err)); }
  };
  const inputCls = "w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";
  const labelCls = "block text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1";

  const insertVar = (v) => {
    const el = bodyRef.current;
    const body = value.body || "";
    if (el && typeof el.selectionStart === "number") {
      const pos = el.selectionStart;
      const next = body.slice(0, pos) + v + body.slice(pos);
      set("body", next);
      requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = pos + v.length; });
    } else {
      set("body", body + v);
    }
  };

  const previewCtx = {
    nome: "Giulia", customer_name: "Giulia Bianchi", data: "25/08", ora: "20:00",
    ristorante: "Ristorante Da Lucia", coupon_name: linkedCouponName || "Sconto 20% Cena", codice: "DH-AB12CD", data_fine: "30/09",
  };
  const renderPreview = (t) => Object.entries(previewCtx).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, v), t || "");

  return (
    <div className="space-y-3" data-testid="message-editor">
      {channel === "email" && (
        <div>
          <label className={labelCls}>Oggetto</label>
          <input value={value.subject || ""} onChange={(e) => set("subject", e.target.value)} className={inputCls} data-testid="msg-subject" />
        </div>
      )}
      <div>
        <label className={labelCls}>Messaggio</label>
        <textarea ref={bodyRef} rows={5} value={value.body || ""} onChange={(e) => set("body", e.target.value)}
          className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
          data-testid="msg-body" />
      </div>
      <div>
        <label className={labelCls}>Variabili dinamiche</label>
        <div className="flex flex-wrap gap-1">
          {VARIABLES.map(({ v, label }) => (
            <button type="button" key={v} onClick={() => insertVar(v)} data-testid={`var-${v.replace(/[{}]/g, "")}`}
              className="h-7 px-2 rounded-full bg-zinc-100 border border-zinc-200 text-[11px] font-medium text-zinc-700 hover:bg-zinc-200 transition">
              {label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={labelCls}>Emoji</label>
        <div className="flex flex-wrap gap-1">
          {EMOJIS.map((e) => (
            <button type="button" key={e} onClick={() => insertVar(e)} data-testid={`emoji-${e.codePointAt(0).toString(16)}`}
              className="w-8 h-8 rounded-md border border-zinc-200 text-base hover:bg-zinc-100 transition">{e}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls}>Testo CTA</label>
          <input value={value.cta_text || ""} onChange={(e) => set("cta_text", e.target.value)} placeholder="Es. Prenota ora"
            className={inputCls} data-testid="msg-cta-text" />
        </div>
        <div>
          <label className={labelCls}>Link CTA</label>
          <input value={value.cta_url || ""} onChange={(e) => set("cta_url", e.target.value)} placeholder="https://..."
            className={inputCls} data-testid="msg-cta-url" />
        </div>
      </div>
      <div>
        <label className={labelCls}>Immagine email (JPEG/PNG) o URL allegato</label>
        <div className="flex gap-2">
          <input value={value.attachment_url || ""} onChange={(e) => set("attachment_url", e.target.value)} placeholder="https://..."
            className={inputCls} data-testid="msg-attachment" />
          {channel === "email" && (
            <>
              <input type="file" accept="image/jpeg,image/png" ref={fileRef} className="hidden"
                onChange={(e) => { uploadImage(e.target.files?.[0]); e.target.value = ""; }} data-testid="msg-image-file" />
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="msg-image-upload"
                className="h-10 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold shrink-0">Carica</button>
            </>
          )}
        </div>
      </div>
      {channel === "email" && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={labelCls}>Testo secondo pulsante</label>
            <input value={value.cta2_text || ""} onChange={(e) => set("cta2_text", e.target.value)} placeholder="Es. Prenota ora"
              className={inputCls} data-testid="msg-cta2-text" />
          </div>
          <div>
            <label className={labelCls}>Link secondo pulsante</label>
            <input value={value.cta2_url || ""} onChange={(e) => set("cta2_url", e.target.value)} placeholder="{web_form_url}"
              className={inputCls} data-testid="msg-cta2-url" />
          </div>
        </div>
      )}
      {!(channel === "whatsapp" && value.wa_template) ? (
      <div>
        <label className={labelCls}>Anteprima</label>
        <div className={`rounded-lg border p-3 ${channel === "whatsapp" ? "bg-[#E7F6E9] border-emerald-200" : "bg-zinc-50 border-zinc-200"}`}
          data-testid="msg-preview">
          {channel === "email" && value.subject && (
            <div className="text-xs font-bold text-zinc-900 mb-1">{renderPreview(value.subject)}</div>
          )}
          <div className="text-sm text-zinc-800 whitespace-pre-wrap">{renderPreview(value.body) || "Scrivi un messaggio..."}</div>
          {value.attachment_url && (
            <img src={value.attachment_url} alt="" className="mt-2 rounded-md max-h-28 object-cover"
              onError={(e) => (e.target.style.display = "none")} />
          )}
          {value.cta_text && (
            <button type="button" className="mt-2 h-9 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold" data-testid="msg-preview-cta">
              {value.cta_text}
            </button>
          )}
          {value.cta2_text && (
            <button type="button" className="mt-2 ml-2 h-9 px-4 rounded-md bg-white border border-zinc-400 text-zinc-900 text-xs font-bold" data-testid="msg-preview-cta2">
              {value.cta2_text}
            </button>
          )}
        </div>
      </div>
      ) : (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-800" data-testid="msg-preview-tpl-notice">
        Hai selezionato un template Meta: il testo libero qui sopra <strong>non verrà inviato</strong>. L'anteprima del template è in basso, sotto le impostazioni.
      </div>
      )}
    </div>
  );
}
